<template>
  <loader :is-loading="loading" />
  <div class="flex grow flex-col space-y-2 overflow-y-auto pb-4">
    <div class="flex items-center space-x-3">
      <lf-h4 data-cy="email-subject">{{ log.data.subject }}</lf-h4>
      <total-indicator
        v-if="log.data.thread_messages_count"
        :value="log.data.thread_messages_count"
      />
    </div>
    <div class="flex flex-col flex-col-reverse gap-y-2">
      <communication-log-email-thread-item
        v-for="(email, index) in threadEmails"
        :key="email.id"
        :email="email"
        :business-name="log.data.emailable?.business?.business_legal_name"
        :open="index === 0"
        data-cy="thread-item"
      />
    </div>
    <div class="flex grow items-start">
      <div
        v-if="!isReplying"
        class="flex cursor-pointer items-center justify-start gap-x-2 text-primary"
        @click="isReplying = true"
      >
        <icon-base :icon="IconReply" />
        <span>{{ $t("COMMON.REPLY") }}</span>
      </div>
      <div v-else class="flex w-full grow">
        <loader :is-loading="loadingModalData" />
        <email-form
          class="grow rounded-sm border"
          @close-dialog="isReplying = false"
        />
      </div>
    </div>
  </div>
  <div v-if="canLoadMore" class="flex w-full items-center justify-center">
    <button class="p-5" @click.stop.prevent="getEmailThreadItems">
      <span class="text-xs text-primary">
        {{ $t("COMMON.LOAD_MORE") }} {{ $t("COMMON.EMAIL", 2) }}
      </span>
    </button>
  </div>
  <div
    v-if="!threadEmails.length"
    class="flex items-center justify-center p-5 text-label"
  >
    {{ $t("COMMON.NO") }}
    {{ $t("COMMON.EMAIL", 2) }}
    {{ $t("COMMON.AVAILABLE") }}
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";

import { usePromiseWrapper } from "@/hooks/common";
import { useUpdateEmailModalDataMutation } from "@/hooks/queries/useInbox";
import emailService from "@/services/modules/emails";

import CommunicationLogEmailThreadItem from "@/components/communicationLogs/communication/emailThread/CommunicationLogEmailThreadItem.vue";
import TotalIndicator from "@/components/deals/dataServices/cfa/moneythumb/TotalIndicator.vue";
import EmailForm from "@/components/emailSending/EmailForm.vue";
import IconReply from "@/components/icons/IconReply.vue";
import Loader from "@/components/ui/Loader.vue";
import LfH4 from "@/components/ui/text/LfHeading4.vue";

import type { IResponseMeta } from "@/models/common";
import type { EmailLog, EmailThreadItem } from "@/models/communicationLogs";

interface Props {
  log: EmailLog;
}

const { log } = defineProps<Props>();

const page = ref(0);
const isReplying = ref(false);
const openedThreadItemsMeta = ref<IResponseMeta | null>(null);
const threadEmails = ref<EmailThreadItem[]>([]);

const canLoadMore = computed(() => {
  if (!openedThreadItemsMeta.value) {
    return false;
  }
  const currentTotal =
    openedThreadItemsMeta.value.current_page *
    openedThreadItemsMeta.value.per_page;
  const to = openedThreadItemsMeta.value?.to;
  return threadEmails.value.length && to && currentTotal <= to;
});

const { fetchWrapper: getEmailThreadItems, loading } = usePromiseWrapper(
  async () => {
    const emailThreadItemsData = await emailService.getEmailThread(
      log.data.email_thread_id
    );
    openedThreadItemsMeta.value = emailThreadItemsData.meta;

    threadEmails.value = threadEmails.value.concat(emailThreadItemsData.data);
    page.value++;
  }
);

const { mutateAsync: getEmailModalData, isPending: loadingModalData } =
  useUpdateEmailModalDataMutation();

onMounted(async () => {
  await getEmailThreadItems();
  if (log.application_id) {
    getEmailModalData({ application_ids: [log.application_id] });
    return;
  }
  if (log.data.email_thread_id) {
    getEmailModalData({ email_thread_ids: [log.data.email_thread_id] });
  }
});
</script>
